import classNames from 'classnames';
import Divider from '@/components/atoms/divider/divider';
import Image from '@/components/atoms/image/image';
import Accent, { AccentProps } from '@/components/molecules/accent/accent';
import { CtaLinkListProps } from '@/components/organisms/cta-link-list/cta-link-list';
import NumberedSectionsText from '../numbered-sections-text/numbered-sections-text';
import styles from './numbered-sections-card.module.scss';

interface NumberedSectionsCardProps {
  /** The class name to apply to the component */
  className?: string;
  /** The text to show */
  contentHtml: string;
  /** An array of links to show */
  ctaLinks?: CtaLinkListProps['items'];
  /** The headline of the section */
  headline: string;
  /** The image to show */
  image: Content.Properties.Image | null;
  /** The index of the card */
  index: number;
  /** An event that is triggered when the card comes in view */
  onViewEnter: () => void;
  /** An event that is triggered when the card exits out of view */
  onViewExit: () => void;
}

const NumberedSectionsCard = ({
  className,
  contentHtml,
  ctaLinks,
  onViewEnter,
  onViewExit,
  headline,
  image,
  index,
}: NumberedSectionsCardProps) => {
  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasImage]: !!image,
      })}
    >
      <div className={styles.divider}>
        <Divider />
      </div>

      <div className={styles.contentWrapper}>
        <Accent
          className={styles.accent}
          text={`${index + 1}`.padStart(2, '0')}
          variant={`Variant${(index % 3) + 1}` as AccentProps['variant']}
        />

        {image && (
          <Image
            alt={image.altText}
            aspectRatio="threeByFour"
            className={styles.image}
            fill
            focalPoint={image.focalPoint}
            height={438}
            src={image.url}
            srcSet={{
              desktop: {
                height: 722,
                width: 541,
              },
              laptop: {
                height: 500,
                width: 375,
              },
              tablet: {
                height: 454,
                width: 341,
              },
            }}
            unoptimized
            width={328}
          />
        )}

        <NumberedSectionsText
          className={styles.text}
          ctaLinks={ctaLinks}
          headline={headline}
          onEnter={onViewEnter}
          onLeave={onViewExit}
          text={contentHtml}
        />
      </div>
    </div>
  );
};

NumberedSectionsCard.displayName = 'NumberedSectionsCard';

export default NumberedSectionsCard;
